import { Mic, StopCircle } from "lucide-react";
import React, {
  useRef,
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";

const REACT_APP_STT_WS_URL =
  process.env.REACT_APP_STT_WS_URL || "ws://localhost:8002/ws/stt/stream";

const LiveTranscribe = forwardRef(
  (
    {
      onTranscriptReady,
      disabled = false,
      className = "",
      styles,
      trackResponse = false,
      language = "en-US", // Default language
    },
    ref
  ) => {
    const wsRef = useRef(null);
    const audioContextRef = useRef(null);
    const mediaStreamRef = useRef(null);
    const processorRef = useRef(null);

    const [isStreaming, setIsStreaming] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [sttError, setSttError] = useState(null);
    const [isMicEnabled, setIsMicEnabled] = useState(false); // New state to track if mic is enabled by user
    const [connectionHealth, setConnectionHealth] = useState(null); // Track connection health

    // --- Public API (optional for parent) ---
    useImperativeHandle(ref, () => ({
      stop: () => stop(),
      start: () => start(),
      enableMic: () => setIsMicEnabled(true),
      disableMic: () => {
        setIsMicEnabled(false);
        stop();
      },
      isStreaming,
      isConnected,
      isMicEnabled,
    }));

    // Remove auto-start useEffect - let user control when to start
    useEffect(() => {
      if (isMicEnabled) {
        start();
      } else {
        stop();
      }
    }, [isMicEnabled]);

    // Remove auto-enable on disabled change
    useEffect(() => {
      console.log("disabled", disabled);
      if (!disabled) {
        console.log("start after disblaed");
        setIsMicEnabled(true);
      }
      return () => {
        setIsMicEnabled(false);
        stop();
      };
    }, [disabled]);

    const start = async () => {
      if (isStreaming || disabled || !isMicEnabled) return; // don't start if disabled or mic not enabled
      setSttError(null);

      console.log("🎙️ Starting STT streaming...");
      
      // Construct WebSocket URL with language parameter
      const wsUrl = `${REACT_APP_STT_WS_URL}?language=${encodeURIComponent(language)}`;
      console.log("WebSocket URL:", wsUrl);
      
      try {
        wsRef.current = new WebSocket(wsUrl);
        wsRef.current.binaryType = "arraybuffer";
      } catch (error) {
        console.error("Failed to create WebSocket:", error);
        setSttError("Failed to create WebSocket connection");
        return;
      }

      wsRef.current.onopen = async () => {
        console.log("✅ STT WS connected");
        setIsConnected(true);

        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
          });
          mediaStreamRef.current = stream;
          console.log("✅ Microphone access granted");
        } catch (error) {
          console.error("❌ Microphone access denied:", error);
          setSttError("Microphone access denied");
          stop();
          return;
        }

        const AudioContext = window.AudioContext || window.webkitAudioContext;
        const audioContext = new AudioContext();
        audioContextRef.current = audioContext;

        const source = audioContext.createMediaStreamSource(mediaStreamRef.current);
        const processor = audioContext.createScriptProcessor(4096, 1, 1);
        processorRef.current = processor;

        processor.onaudioprocess = (event) => {
          if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN)
            return;
          const inputData = event.inputBuffer.getChannelData(0);
          const downsampled = downsampleBuffer(
            inputData,
            audioContext.sampleRate,
            16000
          );
          if (!downsampled) return;
          const pcm16u8 = convertFloat32ToPCM16(downsampled);
          wsRef.current.send(pcm16u8.buffer);
        };

        source.connect(processor);
        processor.connect(audioContext.destination);

        setIsStreaming(true);
        
        // Start connection health check
        startConnectionHealthCheck();
      };

      wsRef.current.onerror = (err) => {
        console.error("❌ WS error", err);
        setSttError("WebSocket connection failed");
        setIsConnected(false);
        setIsStreaming(false);
        stop();
      };

      wsRef.current.onclose = (event) => {
        console.log("🔌 WS closed", event.code, event.reason);
        setIsConnected(false);
        setIsStreaming(false);
        if (event.code !== 1000) {
          setSttError(`Connection closed: ${event.reason || 'Unknown error'}`);
        }
      };

      wsRef.current.onmessage = (ev) => {
        try {
          console.log("📨 Received STT message:", ev.data);
          const data = JSON.parse(ev.data);
          if (data.error) {
            console.error("❌ STT error:", data.error);
            setSttError(data.error);
            setIsConnected(false);
            setIsStreaming(false);
            stop();
            return;
          }
          if (data.transcript && !data.isPartial) {
            console.log("✅ Final transcript received:", data.transcript);
            onTranscriptReady?.(data.transcript); // send only final transcript to parent
            // Auto-disable mic after sending transcript
            setIsMicEnabled(false);
            stop();
          }
        } catch (e) {
          console.error("❌ Message parse error:", e);
          console.log("Raw message:", ev.data);
        }
      };
    };

    const startConnectionHealthCheck = () => {
      const healthCheckInterval = setInterval(() => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          // Send ping to check connection
          try {
            wsRef.current.send(JSON.stringify({ type: "ping" }));
            setConnectionHealth("healthy");
          } catch (error) {
            console.error("Health check failed:", error);
            setConnectionHealth("unhealthy");
            setIsConnected(false);
            setIsStreaming(false);
            clearInterval(healthCheckInterval);
          }
        } else {
          console.log("WebSocket not open, stopping health check");
          setConnectionHealth("disconnected");
          setIsConnected(false);
          setIsStreaming(false);
          clearInterval(healthCheckInterval);
        }
      }, 5000); // Check every 5 seconds

      // Store interval ID for cleanup
      wsRef.current.healthCheckInterval = healthCheckInterval;
    };

    const stop = () => {
      console.log("🛑 Stopping STT stream...");
      
      // Clear health check interval
      if (wsRef.current && wsRef.current.healthCheckInterval) {
        clearInterval(wsRef.current.healthCheckInterval);
      }
      
      try {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ event: "EOS" }));
          wsRef.current.close();
        }
      } catch (e) {}

      wsRef.current = null;

      if (processorRef.current) {
        processorRef.current.disconnect();
        processorRef.current.onaudioprocess = null;
        processorRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((t) => t.stop());
        mediaStreamRef.current = null;
      }

      setIsStreaming(false);
      setIsConnected(false);
      setConnectionHealth(null);
    };

    // auto start/stop based on disabled prop
    // useEffect(() => {
    //   if (disabled) {
    //     stop();
    //   } else {
    //     start();
    //   }
    //   return () => stop();
    //   // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [disabled]);

    // --- helpers ---
    function downsampleBuffer(buffer, sampleRate, outSampleRate) {
      if (outSampleRate === sampleRate) return buffer;
      const sampleRateRatio = sampleRate / outSampleRate;
      const newLength = Math.round(buffer.length / sampleRateRatio);
      const result = new Float32Array(newLength);
      let offsetResult = 0;
      let offsetBuffer = 0;
      while (offsetResult < result.length) {
        const nextOffsetBuffer = Math.round(
          (offsetResult + 1) * sampleRateRatio
        );
        let accum = 0,
          count = 0;
        for (
          let i = offsetBuffer;
          i < nextOffsetBuffer && i < buffer.length;
          i++
        ) {
          accum += buffer[i];
          count++;
        }
        result[offsetResult] = count ? accum / count : 0;
        offsetResult++;
        offsetBuffer = nextOffsetBuffer;
      }
      return result;
    }

    function convertFloat32ToPCM16(float32Array) {
      const l = float32Array.length;
      const buffer = new ArrayBuffer(l * 2);
      const view = new DataView(buffer);
      let offset = 0;
      for (let i = 0; i < l; i++, offset += 2) {
        let s = Math.max(-1, Math.min(1, float32Array[i]));
        view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
      }
      return new Uint8Array(buffer);
    }

    const handleMicClick = () => {
      console.log("🎤 Mic clicked! Current state:", { isMicEnabled, isStreaming, disabled, isConnected });
      
      if (!isMicEnabled) {
        console.log("🔧 Enabling mic and starting...");
        // Enable mic and start streaming
        setIsMicEnabled(true);
        // start();
      } else if (isStreaming) {
        console.log("🛑 Stopping streaming and disabling mic...");
        // Stop streaming and disable mic
        setIsMicEnabled(false);
        // stop();
      } else {
        console.log("▶️ Starting streaming...");
        // Start streaming if mic is enabled but not streaming
        start();
      }
    };

    return (
      <div className={`d-flex align-items-center gap-2 ${className}`}>
        {!isConnected && !disabled && isMicEnabled && (
          <div className="d-flex align-items-center gap-1">
            <div
              className="spinner-border spinner-border-sm text-secondary"
              role="status"
            />
            <small>Connecting...</small>
          </div>
        )}

        {isConnected && isStreaming && (
          <div className="d-flex align-items-center gap-1">
            <div
              className="spinner-border spinner-border-sm text-success"
              role="status"
            />
            <small>Listening...</small>
          </div>
        )}

        {/* {sttError && (
          <small className="text-danger">STT Error: {sttError}</small>
        )} */}
        <div
          className={
            styles.bottomIconWrap + (isStreaming ? " " + styles.active : "")
          }
        >
          <button
            className={styles.bottomIcon}
            disabled={disabled}
            onClick={handleMicClick}
            title={
              !isMicEnabled
                ? "Enable microphone"
                : isStreaming
                ? "Stop listening"
                : "Start listening"
            }
          >
            {!isMicEnabled ? (
              <Mic size={20} color="#666" />
            ) : !isStreaming ? (
              <Mic size={20} color="#0E46A3" />
            ) : (
              <StopCircle size={20} color="#0e46a3" />
            )}
          </button>
        </div>
      </div>
    );
  }
);

export default LiveTranscribe;
